import cookieManager from '@/common/cookieManager'
import utils from '@/common/utils'

const SESSION_KEY = 'h5_bind_context'
const COOKIE_KEY = 'h5_bind'
const COOKIE_DAYS = 7

/** @type {null | { domain: string, macid: string, siteKey: string, customerName: string }} */
let runtimeBind = null

function normalizeBind(bind = {}) {
  return {
    domain: String(bind.domain || '').trim(),
    macid: String(bind.macid || '').trim(),
    siteKey: String(bind.siteKey || '').trim(),
    customerName: String(bind.customerName || '').trim()
  }
}

function isValidBind(bind) {
  return !!(bind.domain && bind.macid)
}

/** 登录前：仅内存暂存（不写 Cookie / sessionStorage） */
export function setRuntimeH5Bind(bind = {}) {
  const normalized = normalizeBind(bind)
  runtimeBind = isValidBind(normalized) ? normalized : null
  return runtimeBind
}

export function getRuntimeH5Bind() {
  return runtimeBind
}

/** 读取绑定上下文：内存 → sessionStorage → Cookie */
export function getH5BindContext() {
  if (runtimeBind) {
    return { ...runtimeBind }
  }

  const fromSession = readFromSession()
  if (fromSession) {
    runtimeBind = fromSession
    return { ...fromSession }
  }

  const fromCookie = readFromCookie()
  if (fromCookie) {
    runtimeBind = fromCookie
    writeToSession(fromCookie)
    return { ...fromCookie }
  }

  return null
}

/** 登录成功后持久化 */
export function persistH5Bind(bind = {}) {
  const normalized = normalizeBind(bind)
  if (!isValidBind(normalized)) {
    return null
  }
  runtimeBind = normalized
  writeToSession(normalized)
  cookieManager.set(COOKIE_KEY, JSON.stringify(normalized), { expires: COOKIE_DAYS })
  return normalized
}

/** 应用启动时恢复（H5 页刷新） */
export function restoreH5Bind() {
  return getH5BindContext()
}

export function clearH5Bind() {
  runtimeBind = null
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch (e) {
    // ignore
  }
  cookieManager.remove(COOKIE_KEY)
}

export function getH5Domain() {
  const ctx = getH5BindContext()
  return ctx ? ctx.domain : ''
}

/** 业务 macid（明文，直接用于请求） */
export function getH5Macid() {
  const ctx = getH5BindContext()
  return ctx ? ctx.macid : ''
}

/** 站点密钥密文（原样存储） */
export function getH5SiteKeyCipher() {
  const ctx = getH5BindContext()
  return ctx ? ctx.siteKey : ''
}

/** 使用时解密 siteKey */
export function resolveH5SiteKey(apitype = 'default') {
  const cipher = getH5SiteKeyCipher()
  return cipher ? utils.decryptKey(cipher, apitype) : ''
}

/** 中心 MiniAPI（始终走 eqoa + 中心 macid） */
export function isH5MiniApiUrl(url = '') {
  return /\/E5MiniAPI\//i.test(String(url))
}

function normalizeHost(domain = '') {
  let host = String(domain).trim()
  if (!host) return ''
  if (!/^https?:\/\//i.test(host)) {
    host = `http://${host}`
  }
  return host.replace(/\/+$/, '')
}

/**
 * H5 业务接口：与 PC direct / proxy 一致，去掉 Q7FileAPI 前缀
 * /Q7FileAPI/E5API/... → {bindDomain}/E5API/...
 */
export function resolveH5ApiUrl(url = '') {
  if (!url || isH5MiniApiUrl(url) || /^https?:\/\//i.test(url)) {
    return url
  }
  const host = normalizeHost(getH5Domain())
  if (!host) {
    return url
  }
  const normalized = String(url).trim().startsWith('/')
    ? String(url).trim()
    : `/${String(url).trim()}`
  const match = normalized.match(/^\/Q7FileAPI\/(.+)$/i)
  if (!match) {
    return url
  }
  const path = match[1]
  return `${host.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
}

function writeToSession(bind) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(bind))
  } catch (e) {
    // ignore
  }
}

function readFromSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = normalizeBind(JSON.parse(raw))
    return isValidBind(parsed) ? parsed : null
  } catch (e) {
    return null
  }
}

function readFromCookie() {
  try {
    const raw = cookieManager.get(COOKIE_KEY)
    if (!raw) return null
    const parsed = normalizeBind(JSON.parse(raw))
    return isValidBind(parsed) ? parsed : null
  } catch (e) {
    return null
  }
}
